import { createContext, useContext, useState } from "react";
import { isLoggedIn } from "../utils/auth";
import { AuthAPI } from "../api/AuthAPI";
import { getSessionId } from "../utils/cartKeys";

const CartContext = createContext();

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  const [cart, setCart] = useState(null);

  const [cartOpen, setCartOpen] = useState(false);

  const count = items.reduce((sum, i) => sum + i.qty, 0);

  /* ================= FETCH CART ================= */

  const fetchServerCart = async () => {
    try {
      const res = await AuthAPI.afterlogincart({
        params: isLoggedIn()
          ? {}
          : {
              session_id: getSessionId(),
            },
      });

      const cartData = res?.data?.data || null;

      const finalCart = cartData
        ? {
            ...cartData,
            first_order_discount_config:
              res?.data?.first_order_discount_config || null,
            first_order_discount_used:
              res?.data?.first_order_discount_used ?? true,
            user: res?.data?.user || null,
          }
        : null;

      setCart(finalCart);

      // IMPORTANT: keep every field CartPage.jsx (and anything else
      // rendering line items) actually reads. Previously this dropped
      // `sku` and `total_price` completely, and renamed `product_name` ->
      // `name` / `unit_price` -> `price`, which meant the UI had nothing
      // to read and fell back to blank / ₹0.
      const normalized = (cartData?.items || []).map((i) => ({
        id: i.id,
        cart_id: i.cart_id,
        product_id: i.product_id,
        product_name: i.product_name,
        sku: i.sku,
        qty: i.qty,
        unit_price: Number(i.unit_price),
        total_price: Number(i.total_price ?? Number(i.unit_price) * i.qty),
        stock: i.product?.stock ?? Infinity,
        image: i.image || i.product?.images?.[0]?.image || "",
        product: i.product || null, // keep full product object (stock, images[] gallery, etc.)

        // legacy aliases kept for any other component still reading these
        name: i.product_name,
        price: Number(i.unit_price),

        isGuest: !isLoggedIn(),
      }));

      setItems(normalized);
    } catch (e) {
      console.error(e);

      setCart(null);

      setItems([]);
    }
  };

  /* ================= CLEAR CART ================= */

  const clearCart = () => {
    setItems([]);

    setCart(null);
  };

  /* ================= ADD TO CART ================= */

  const addToCart = async (product, qty = 1) => {
    const stock = Number(product.stock ?? Infinity);

    if (qty > stock) {
      alert("Requested quantity exceeds available stock");

      return;
    }

    await AuthAPI.addtocart({
      product_id: product.id,
      qty,

      ...(isLoggedIn()
        ? {}
        : {
            session_id: getSessionId(),
          }),
    });

    await fetchServerCart();

    // setCartOpen(true);
  };

  /* ================= INCREMENT ================= */

  const incrementQty = async (item) => {
    if (item.qty >= item.stock) return;

    await AuthAPI.updatecart(item.id, {
      qty: item.qty + 1,
    });

    await fetchServerCart();
  };

  /* ================= DECREMENT ================= */

  const decrementQty = async (item) => {
    if (item.qty === 1) {
      removeFromCart(item);

      return;
    }

    await AuthAPI.updatecart(item.id, {
      qty: item.qty - 1,
    });

    await fetchServerCart();
  };

  /* ================= REMOVE ================= */

  const removeFromCart = async (item) => {
    await AuthAPI.deletecart(item.id);

    await fetchServerCart();
  };

  const updateQty = async (item, qty) => {
    if (qty < 1) {
      return removeFromCart(item);
    }

    if (qty === item.qty) return; // nothing changed, skip the call

    await AuthAPI.updatecart(item.id, {
      qty,
    });

    await fetchServerCart();
  };
  /* ================= SYNC GUEST ================= */

  const syncGuestCartAfterLogin = async () => {
    await AuthAPI.syncCart({
      session_id: getSessionId(),
    });

    // remove guest session after successful sync
    localStorage.removeItem("session_id");
    await fetchServerCart();
  };

  /* ================= TOTAL ================= */

  const totalAmount =
    cart?.grand_total ||
    items.reduce((sum, i) => sum + Number(i.unit_price) * i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        items,
        count,

        cartOpen,

        openCart: async () => {
          setCartOpen(true);

          await fetchServerCart();
        },

        closeCart: () => setCartOpen(false),

        fetchServerCart,

        addToCart,

        incrementQty,

        decrementQty,
  updateQty,
        removeFromCart,

        syncGuestCartAfterLogin,

        totalAmount,

        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);